import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import useMeasure from "react-use-measure";

import ProgressBar from 'react-bootstrap/ProgressBar';
import Dropzone, { DropzoneRef } from "react-dropzone";
import { nanoid } from "nanoid";
import S3Upload from "react-s3-uploader/s3upload";
import { animated, useSpring, useTransition } from "@react-spring/web";

import Text from "src/components/text";
import Slider from "src/components/slider";
import Thumbnail from "src/components/thumbnail";
import Actions from "src/components/actionbar";
import Button from "src/components/button";

import { FileService } from "src/utils/file.service";
import AuthService from "src/utils/auth.service";
import FileUploadService from "src/utils/file.upload.service";
import InfiniteScroll from "src/components/infiniteScroll";

import { Simulation } from "src/types/coaching/simulation";
import { AssetFile } from "src/types/assets";

import { SimulationContext } from "../../context";
import { deleteCallRecording, fetchCallRecording, fetchCallRecordings, generateTranscriptsForCallRecording, syncCallRecordingProcessingState } from "src/redux/actions/coaching/callRecording";

import {invalidateShuffleObjectivesState, invalidateShuffleOrganizationState, invalidateShufflePersonaState, shuffleObjectives, shuffleOrganization, shufflePersona, updateSimulationProperty, updateValidationErrorsSimulation ,clearObjectives, clearOrganization, clearPrsona} from "src/redux/actions/coaching/simulation";
import { validateSimulationRequest } from "../../helper";
import {uploadAsset, uploadAssetFailed, uploadAssetProgress, uploadingAssetSucceeded } from "src/redux/actions/assets";

import dateformat from "dateformat";
import { CallRecording } from "src/types/coaching/callRecording";

import _ from "lodash";
import cx from "classnames";
import styles from "./scenarios.module.scss";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/2.0`;

interface ScenarioSettingsProps {
  simulation: Simulation;
}

export const ScenarioSettings: React.FC<ScenarioSettingsProps> = ({ simulation }) => {
  const dispatch = useDispatch();
  const simulationContext = React.useContext(SimulationContext);

  const description = useMemo(() => {
    return simulation.description || "";
  }, [simulation]);

  const { shufflingOrganization, shuffledOrganization } = useSelector((state: any) => {
    const shufflingOrganization = _.get(state, `coaching.simulations.edit.shuffle.organization.loading`);
    const shuffledOrganization = _.get(state, `coaching.simulations.edit.shuffle.organization.item`);

    return {
      shufflingOrganization,
      shuffledOrganization
    }
  }, _.isEqual);

  const { shufflingPersona, shuffledPersona } = useSelector((state: any) => {
    const shufflingPersona = _.get(state, `coaching.simulations.edit.shuffle.persona.loading`);
    const shuffledPersona = _.get(state, `coaching.simulations.edit.shuffle.persona.item`);

    return {
      shufflingPersona,
      shuffledPersona
    }
  }, _.isEqual);

  const { shufflingObjectives, shuffledObjectives } = useSelector((state: any) => {
    const shufflingObjectives = _.get(state, `coaching.simulations.edit.shuffle.objectives.loading`);
    const shuffledObjectives = _.get(state, `coaching.simulations.edit.shuffle.objectives.item`);

    return {
      shufflingObjectives,
      shuffledObjectives
    }
  }, _.isEqual);

  const shuffling = shufflingOrganization || shufflingPersona || shufflingObjectives;

  const {callRecording, loading, loaded, error} = useSelector((state: any) => {
    const callRecordingState = _.get(state, `coaching.callRecordings.call`) || {};
    const callRecording = _.get(callRecordingState, `item`);
    const loading = _.get(callRecordingState, `loading`);
    const loaded = _.get(callRecordingState, `loaded`);
    const error = _.get(callRecordingState, ` error`);

    return {
      callRecording,
      loading,
      loaded,
      error
    }
  }, _.isEqual);

  const [openSourceSlider, setOpenSourceSlider] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);

  const handleAddSourceClick = () => {
    setOpenSourceSlider(true);
  };

  const handleEditOrganizationInfo = () => {
    if (simulationContext.editOrganizationInfo) {
      simulationContext.editOrganizationInfo(true);
    }
  }

  const handleEditPersona = () => {
    if (simulationContext.editPersona) {
      simulationContext.editPersona(true);
    }
  }

  const handleEditObjectives = () => {
    if (simulationContext.editObjectives) {
      simulationContext.editObjectives(true);
    }
  }

  const handleShuffle = () => {
    handleShufflePersona();
    handleShuffleOrganization();
    handleShuffleObjectives();
  }

  const handleShufflePersona = () => {
    dispatch(shufflePersona(simulation!.id!));
  }

  const handleShuffleOrganization = () => {
    dispatch(shuffleOrganization(simulation!.id!));
  }

  const handleShuffleObjectives = () => {
    dispatch(shuffleObjectives(simulation!.id!));
  }

  const handleDismiss = () => {
    setGenerating(false);
    setGenerated(false);
  };

  const handleEraseOrganizationData = () => {
    dispatch(clearOrganization())
  }
  const handleErasePersonaData = () => {
    dispatch(clearPrsona())
  }
  const handleEraseObjectivesData = () => {
    dispatch(clearObjectives());
  }

  useEffect(() => {
    if (shuffledPersona) {
      dispatch(updateSimulationProperty({ simulation: simulation.id!, property: "persona", value: shuffledPersona }));
      dispatch(invalidateShufflePersonaState(simulation!.id!));
    }
  }, [shuffledPersona]);

  useEffect(() => {
    if (shuffledOrganization) {
      dispatch(updateSimulationProperty({ simulation: simulation.id!, property: "organization", value: shuffledOrganization }));
      dispatch(invalidateShuffleOrganizationState(simulation!.id!));
    }
  }, [shuffledOrganization]);

  useEffect(() => {
    if (shuffledObjectives) {
      dispatch(updateSimulationProperty({ simulation: simulation.id!, property: "objectives", value: shuffledObjectives }));
      dispatch(invalidateShuffleObjectivesState(simulation!.id!));
    }
  }, [shuffledObjectives]);

  useEffect(() => {
    if(loading){
      setGenerating(true);
    }
  }, [loading]);

  useEffect(() => {
  const errors = validateSimulationRequest(simulation);
  dispatch(updateValidationErrorsSimulation({
    simulation: simulation.id!,
    errors,
  }));
}, [simulation]);

  
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {!generated && !generating && (
          <>
            <div className={styles.title}>
              Auto-fill scenario details from your real calls
            </div>
            <span className={styles.note}>
              Note: You can review and edit any hallucinated details as needed.
            </span>
          </>
        )}

        {generating && (
          <ProfileGenerationSteps
            simulation={simulation}
            onComplete={() => {
              setGenerating(false);
              setGenerated(true);
            }}
          />
        )}

        {generated && (
          <div>
            <h3 className={styles.title}>scenario generated</h3>

            <span className={styles.note}>
              Default voice applied. Choose a voice that matches your buyer for a more realistic simulation.
            </span>

            <div className={styles.actionsContainer}>
              <Button
                className={cx(styles.dismiss)}
                text="dismiss"
                onClick={handleDismiss}
              />
            </div>
          </div>
        )}

        {!generated && !generating &&(
          <div className={styles.actionsContainer}>
            <Button
              className={cx(styles.action)}
              icon="paperflite-add"
              text="import call"
              onClick={handleAddSourceClick}
            />

            <div className={styles.divider}></div>

            <Button
              className={styles.shuffle}
              icon="paperflite-shuffle"
              text={shuffling ? "shuffling..." : "shuffle scenario"}
              onClick={handleShuffle}
            />
          </div>
        )}
      </div>
      
      <div className={styles.body}>
        <div className={styles.setting}>
          <div className={styles.type}>Company profile (optional)</div>
          <ShuffleAnimatedContainer
            shuffle={shufflingOrganization}
            placeholder={OrganizationInfoPlaceholder}

          >
            <OrganizationInfoCard
              simulation={simulation}
              onEdit={handleEditOrganizationInfo}
              onShuffle={handleShuffleOrganization}
              onErase={handleEraseOrganizationData}
            />
          </ShuffleAnimatedContainer>
        </div>
        <div className={styles.setting}>
          <div className={styles.type}>Buyer profile</div>
          <ShuffleAnimatedContainer
            shuffle={shufflingPersona}
            placeholder={PersonaPlaceholder}
          >
            <PersonaCard
              simulation={simulation}
              onEdit={handleEditPersona}
              onShuffle={handleShufflePersona}
              onErase={handleErasePersonaData}
            />
          </ShuffleAnimatedContainer>
        </div>
        <div className={styles.setting}>
          <div className={styles.type}>Buyer needs</div>
          <ShuffleAnimatedContainer
            shuffle={shufflingObjectives}
            placeholder={ObjectivesPlaceholder}
          >
            <ObjectivesCard
              simulation={simulation}
              onEdit={handleEditObjectives}
              onShuffle={handleShuffleObjectives}
              onErase={handleEraseObjectivesData}
            />
          </ShuffleAnimatedContainer>
        </div>
      </div>

      <AddSourceSlider
        open={openSourceSlider}
        onClose={() => setOpenSourceSlider(false)}   
      />
    </div>
  );
}

interface ShuffleAnimatedContainerProps {
  children: React.ReactNode;
  shuffle: boolean;
  placeholder: React.ComponentType;
}

const ShuffleAnimatedContainer: React.FC<ShuffleAnimatedContainerProps> = ({ shuffle, children, placeholder }) => {
  const [height, setHeight] = useState(100); // Placeholder height, adjust as needed
  const [cardRef, cardBounds] = useMeasure();

  const elementTransition = useTransition(!shuffle, {
    from: { transform: "translateY(0%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(-100%)", opacity: 1 },
    onRest: () => {

    },
  });

  const placeholderTransition = useTransition(shuffle, {
    from: { transform: "translateY(100%)", opacity: 1 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(0%)", opacity: 0 }
  });

  useEffect(() => {
    if (!shuffle) {
      setHeight(cardBounds.height);
    }
  }, [shuffle, cardBounds]);

  return (
    <div className={cx(styles.shuffleContainer)}>
      {elementTransition((style, item) =>
        item ? (
          <animated.div ref={cardRef} style={style} className={cx(styles.wrapper)}>
            {children}
          </animated.div>
        ) : false)}
      {placeholderTransition((style, item) =>
        item ? (
          <animated.div style={style} className={cx(styles.wrapper)}>
            <ShufflePlaceholderContainer placeholder={placeholder} height={400} />
          </animated.div>
        ) : false)}
    </div>
  );
}

interface ShufflePlaceholderContainerProps {
  height: number;
  placeholder: React.ComponentType;
}

const ShufflePlaceholderContainer: React.FC<ShufflePlaceholderContainerProps> = ({ height, placeholder }) => {
  const cards = 8;
  const [index, setIndex] = React.useState(0);
  const order = useMemo(() => {
    return _.shuffle(_.times(cards));
  }, []);

  const [props, api] = useSpring(() => ({
    delay: 800,
    from: { transform: "translateY(0%)" },
    to: async (next) => {
      const orderValue = order[index];
      const translateY = orderValue * -100;
      await next({ transform: `translateY(${translateY}%)` });
    },
  }), [index]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % cards);
    }, 1600); // Change every second

    return () => clearInterval(interval);
  }, []);

  return (
    <animated.div className={cx(styles.shufflePlaceholders)} style={{ ...props, height }}>
      {_.times(cards, (index) => (
        <div key={index} className={cx(styles.wrapper)} style={{ height }}>
          {React.createElement(placeholder)}
        </div>
      ))}
    </animated.div>
  );
}

interface OrganizationInfoCardProps {
  simulation: Simulation;
  onEdit: () => void;
  onShuffle: () => void;
  onErase: () => void;
}

export const OrganizationInfoCard: React.FC<OrganizationInfoCardProps> = ({ simulation, onEdit, onShuffle, onErase }) => {
  const organization = useMemo(() => {
    return simulation.organization;
  }, [simulation]);

  const hasOrgData = hasConfigurationData(organization!);
  const eraseTransition = EraseTransition(hasOrgData);

  return (
    <div className={cx(styles.card, styles.organizationInfo)}>
      <div className={styles.header}>
        <div className={styles.name}>
          <i className={cx(styles.icon, "paperflite-buildings")} />
          <Text text={organization?.name || "no name"} lines={2} className={styles.label} />
        </div>
      </div>
      <div className={styles.body}>
        <Text
          className={cx(styles.summary, {
            [styles.disabled]: !organization?.description,
          })}
          text={
            organization?.description ||
            "Create a company profile - define the industry, size, revenue, and tech stack."
          }
          lines={10} 
          tooltip={false}
        />
      </div>
      <div className={styles.actions}>
        <Actions.Row className={styles.iconButton}>
          <Actions.IconButton
            icon="paperflite-edit"
            tooltip="Edit"
            onClick={onEdit}
          />
          <Actions.IconButton
            icon="paperflite-shuffle"
            tooltip="Shuffle"
            onClick={onShuffle}
          />
          {eraseTransition( (style, show) =>
            show && (
              <animated.div style={style}>
                <Actions.IconButton
                  icon="paperflite-erase"
                  tooltip="Erase"
                  onClick={onErase}
                />
              </animated.div>
            )
          )}
        </Actions.Row>
      </div>
    </div>
  );
}

const OrganizationInfoPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.card, styles.organizationInfo, styles.placeholder)}>
      <div className={styles.header}>
        <div className={cx(styles.icon, styles.block)} />
        <div className={cx(styles.name, styles.block)}></div>
      </div>
      <div className={styles.body}>
        <div className={styles.summary}>
          <div className={cx(styles.block)}></div>
          <div className={cx(styles.block)}></div>
          <div className={cx(styles.block)}></div>
        </div>
      </div>
    </div>
  );
}

interface PersonaCardProps {
  simulation: Simulation;
  onEdit: () => void;
  onShuffle: () => void;
  onErase: () => void;
}

export const PersonaCard: React.FC<PersonaCardProps> = ({ simulation, onEdit, onShuffle, onErase }) => {
  const persona = useMemo(() => {
    return simulation.persona;
  }, [simulation]);

  const hasPersonaData = hasConfigurationData(_.omit(persona, 'voice', 'personality'));
  const eraseTransition = EraseTransition(hasPersonaData)

  const callType = _.get(simulation,'type','discovery')

  return (
    <div className={cx(styles.card, styles.persona)}>
      <div className={styles.header}>
        { persona?.icon ? <Thumbnail media={persona?.icon} defaultImage="/images/assets/simulationThumbnail.jpg" 
          className={cx(styles.icon, styles.image)}/> : <i className={cx(styles.icon, "paperflite-user-settings")} />}
        <div className={styles.name}>
          <Text text={persona?.name || "no name"} lines={2} />
        </div>
      </div>
      <div className={styles.body}>
        <Text
          className={cx(styles.summary, {
            [styles.disabled]: !persona?.description,
          })}
          text={
            persona?.description ||
            "Create a buyer profile - define personality, voice, objection tendencies and success metrics."
          }
          lines={10} 
          tooltip={false}
        />
      </div>
      <div className={styles.actions}>
        <Actions.Row className={styles.iconButton}>
          <Actions.IconButton
            icon="paperflite-edit"
            tooltip="Edit"
            onClick={onEdit}
          />
          <Actions.IconButton
            icon="paperflite-shuffle"
            tooltip="Shuffle"
            onClick={onShuffle}
          />
          {eraseTransition( (style, show) =>
            show && (
              <animated.div style={style}>
                <Actions.IconButton
                  icon="paperflite-erase"
                  tooltip="Erase"
                  onClick={onErase}
                />
              </animated.div>
            )
          )}
        </Actions.Row>
      </div>
    </div>
  );
}

const PersonaPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.card, styles.persona, styles.placeholder)}>
      <div className={styles.header}>
        <div className={cx(styles.icon, styles.block)} />
        <div className={cx(styles.name, styles.block)}></div>
      </div>
      <div className={styles.body}>
        <div className={styles.summary}>
          <div className={cx(styles.block)}></div>
          <div className={cx(styles.block)}></div>
          <div className={cx(styles.block)}></div>
        </div>
      </div>
    </div>
  );
}

interface ObjectivesCardProps {
  simulation: Simulation;
  onEdit: () => void;
  onShuffle: () => void;
  onErase: () => void
}

export const ObjectivesCard: React.FC<ObjectivesCardProps> = ({ simulation, onEdit, onShuffle, onErase }) => {
  const objectives = useMemo(() => {
    return simulation.objectives;
  }, [simulation]);

  const hasObjectiveData = hasConfigurationData(objectives!);
  const eraseTransition = EraseTransition(hasObjectiveData)

  return (
    <div className={cx(styles.card, styles.objectives)}>
      <div className={styles.header}>
        <div className={styles.name}>
          <div className={styles.icon}>
            <i className={cx( "paperflite-objectives")} />
          </div>
          Objectives
        </div>
      </div>
      <div className={styles.body}>
        <Text
          className={cx(styles.summary, {
            [styles.disabled]: !objectives?.description,
          })}
          text={
            objectives?.description ||
            "Define buyer needs - objectives, pain points, oppurtunity triggers and deal-breakers."
          }
          lines={10}
          tooltip={false}
        />
      </div>
      <div className={styles.actions}>
        <Actions.Row className={styles.iconButton}>
          <Actions.IconButton
            icon="paperflite-edit"
            tooltip="Edit"
            onClick={onEdit}
          />
          <Actions.IconButton
            icon="paperflite-shuffle"
            tooltip="Shuffle"
            onClick={onShuffle}
          />
          {eraseTransition( (style, show) =>
            show && (
              <animated.div
                style={style}
              >
                <Actions.IconButton
                  icon="paperflite-erase"
                  tooltip="Erase"
                  onClick={onErase}
                />
              </animated.div>
            )
          )}
        </Actions.Row>
      </div>
    </div>
  );
}

const ObjectivesPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.card, styles.objectives, styles.placeholder)}>
      <div className={styles.header}>
        <div className={cx(styles.icon, styles.block)} />
        <div className={cx(styles.name, styles.block)}></div>
      </div>
      <div className={styles.body}>
        <div className={styles.summary}>
          <div className={cx(styles.block)}></div>
          <div className={cx(styles.block)}></div>
          <div className={cx(styles.block)}></div>
        </div>
      </div>
    </div>
  );
}

interface AddSourceSliderProps {
  open: boolean;
  onClose: () => void;    
}

interface DeleteState {
  show: boolean;
  file: CallRecording | null;
  isClosing: boolean;
}

const AddSourceSlider: React.FC<AddSourceSliderProps> = ({ open, onClose}) => {
  const dispatch = useDispatch();
  const dropZoneRef = React.useRef<DropzoneRef>(null);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<any | null>(null);
  const [failed, setFailed] = useState(false);
  const [deleteState, setDeleteState] = useState<DeleteState>({ show: false, file: null, isClosing: false });

  const {
    callRecordingsLoading,
    callRecordingsLoaded,
    callRecordings,
    totalRecordsCount,
    currentPage,
    nextPage,
    hasMoreItems,
    uploadingCallRecording
  } = useSelector((state: any) => {
    const callRecordingsState = _.get(state.coaching, "callRecordings") || {};
    const loading = _.get(callRecordingsState, "loading");
    const loaded = _.get(callRecordingsState, "loaded");
    const pagination = _.get(callRecordingsState, "pagination");
    const totalRecordsCount = _.get(pagination, "totalRecordsCount", 0);
    const currentPage = _.get(pagination, "currentPage", 0);
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");
    const uploadingCallRecording = _.get(callRecordingsState, `upload.${uploadedFile?.id}.uploading`, false)

    const callRecordings = _.get(callRecordingsState, "items") || [];

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      callRecordingsLoading: loading,
      callRecordingsLoaded: loaded,
      callRecordings: callRecordings,
      totalRecordsCount: totalRecordsCount,
      currentPage: currentPage,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems,
      uploadingCallRecording: uploadingCallRecording,
    };
  }, _.isEqual);

  const handleBrowseClick = () => {
    dropZoneRef.current?.open();
  };

  const handleFileUploadFinish = (file, data, signResult: any) => {
    setUploading(false);
    setUploadProgress(100);
    FileUploadService.markFinish(data.id);
    dispatch(
      uploadingAssetSucceeded({
        id: data.id,
      }),
    );

    dispatch(
      generateTranscriptsForCallRecording({
        signedUrl: file.signedUrl,
        id: data?.id,
      }),
    );
  };

  const handleFileUploadError = (file, error: any) => {
    dispatch(
      uploadAssetFailed({
        id: file.id,
        error: error,
      }),
    );
    setUploading(false);
    setFailed(true);
  };

  const handleFileUploadProgress = (progress: number, file) => {
    dispatch(
      uploadAssetProgress({
        id: file.id,
        progress: progress,
      }),
    );
    setUploadProgress(progress);
  };

  const uploadFile = (file) => {
    file.id = nanoid();
    const fileName = _.get(file, "name", "Untitled");
    let fileType = FileService.getFileExtension(fileName);
    if (file.type == "" || file.type == null || file.type == undefined) {
      fileType = "application/octet-stream";
      file = new File([file], fileName, { type: fileType });
      file.id = nanoid();
    }

    const s3Uploader = new S3Upload({
      signingUrl: "/file_upload/initiate",
      signingUrlMethod: "POST",
      signingUrlHeaders: {
        Authorization: `Bearer ${AuthService.getAccessToken()}`,
      },
      server: apiUrl,
      scrubFilename: (filename) => {
        const name = filename.replace(/[^\w\d_\-.\s]+/gi, "");
        return encodeURIComponent(name);
      },
      signingUrlQueryParams: { originalName: encodeURIComponent(fileName) },
      contentDisposition: "auto",
      uploadRequestHeaders: {},
      onFinishS3Put: handleFileUploadFinish,
      onProgress: handleFileUploadProgress,
      onError: handleFileUploadError,
      files: [file],
    });

    FileUploadService.trackUpload(s3Uploader);

    dispatch(
      uploadAsset({
        file: {
          id: file.id,
          name: fileName,
          type: fileType,
          size: file.size,
        } as AssetFile,
      }),
    );
  };

  const handleDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setUploadedFile(file);
      setUploading(true);
      uploadFile(file);
    }
  };

  const handleLoadMoreCallRecordings = () => {
    if (nextPage) {
      const request = {
        page: nextPage,
        limit: 10,
      };
      dispatch(fetchCallRecordings(request));
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteState.file?.id) {
      dispatch(deleteCallRecording({
        id: deleteState.file.id,
      }));

      setDeleteState({
        show: false,
        file: null,
        isClosing: false,
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteState(prev => ({ ...prev, isClosing: true }));
    setTimeout(() => {
      setDeleteState({
        show: false,
        file: null,
        isClosing: false,
      });
    }, 300);
  };
  
  const handleView = (call) => {
    dispatch(fetchCallRecording({
      id: call.id,
    }));
    onClose();
  };

  const handleDelete = (call) => {
    if (call.id) {
      setDeleteState({
        show: true,
        file: call,
        isClosing: false,
      });
    }
  };

  useEffect(() => {
    if (!open || _.size(callRecordings) > 0) {
      return;
    }
    
    const request = {
      page: 0, 
      limit: 10 
    };
    dispatch(fetchCallRecordings(request));
  }, [open]);
  
  return (
    <Slider.Dialog
      className={styles.addSourceContainer}
      width="600px"
      show={open}
      onClose={onClose}
    >
      <Slider.Header>
        <Dropzone
          ref={dropZoneRef}
          noClick
          multiple={false}
          onDrop={handleDrop}
          accept={{
            "video/mp4": [".mp4"],
            "audio/mpeg": [".mp3"],
            "audio/wav": [".wav"],
          }}
        >
          {({ getRootProps, getInputProps }) => (
            <div {...getRootProps()} className={styles.headerContainer}>
              <input {...getInputProps()} type="file" />

              <div className={styles.header}>
                <span className={styles.title}>call library</span>
                <i className="paperflite-close" onClick={onClose} />
              </div>
              <div className={styles.upload}>
                <div className={styles.details}>
                  <div className={styles.title}>
                    <i className="paperflite-info" />
                    <span className={styles.info}>supported formats</span>
                  </div>
                  <span className={styles.message}>
                    You can upload MP3 or MP4 call recordings.
                  </span>
                </div>

                <div className={styles.actions}>
                  <button
                    className={styles.uploadButton}
                    onClick={handleBrowseClick}
                  >
                    <i className="paperflite-upload"></i>
                    <span className={styles.text}>Upload a new call</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </Dropzone>
      </Slider.Header>

      <Slider.Body className={styles.body}>
        <div className={styles.title}>uploaded calls</div>
        {uploadedFile && uploading && (
          <InProgressCallRecordingItem
            uploadedFile={uploadedFile}
            uploading={uploading}
            progress={uploadProgress}
          />
        )}
        {failed && (
          <ProfileGenerationFailed
            uploadedFile={uploadedFile}
            onDelete={() => setFailed(false)}
          />
        )}
        <div className={styles.callList}>
          {!callRecordingsLoading && _.size(callRecordings) === 0 && (
            <div className={styles.emptyMessage}>
              <div className={styles.text}>no calls yet</div>
              <span className={styles.subText}>
                upload a call to get started.
              </span>
            </div>
          )}
          <InfiniteScroll
            className={styles.list}
            loading={callRecordingsLoading}
            hasMore={hasMoreItems}
            loadMore={handleLoadMoreCallRecordings}
            loader={() => {
              return _.times(
                _.size(callRecordings) === 0 || currentPage === 0 ? 7 : 1,
                (index) => <CallRecordingPlaceholder key={index} />,
              );
            }}
          >
            {uploadingCallRecording && (
              <CallRecordingPlaceholder />
            )}

            {(callRecordings || []).map((call: CallRecording) => {
              return (
                <CallRecordingItem
                  key={call.id}
                  call={call}
                  handleDelete={(call) => handleDelete(call)}
                  handleView={handleView}
                />
              );
            })}
          </InfiniteScroll>
        </div>
      </Slider.Body>
      <Slider.Actions>
        {deleteState.show && (
          <DeleteConfirmationDialog
            item={deleteState}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
          />
        )}
        {!deleteState.show && (
          <Button variant="secondary" text="Close" onClick={onClose}></Button>
        )}
      </Slider.Actions>
    </Slider.Dialog>
  );
};

export default AddSourceSlider;

const getCallRecordingName = (call) => {
  if (!call) return "";

  const buyer =
    call.buyerName?.trim() ||
    call.persona?.name?.trim();
  
  const company =
    call.companyName?.trim() ||
    call.organization?.name?.trim();

  if (buyer && company) return `${buyer} from ${company}`;
  if (buyer) return buyer;
  if (company) return company;

  return call.name || "";
};

interface DeleteConfirmationDialogProps {
  item: any;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  item,
  onConfirm,
  onCancel,
}) => {
  const name = getCallRecordingName(item.file);

  const deleting = useSelector((state: any) => {
    return _.get(
      state.coaching,
      `callRecordings.deletion['${item?.file?.id}'].deleting`,
    ) as boolean;
  });

  return (
    <div>
      <div className={`${styles.deleteConfirmation} ${item.isClosing ? styles.closing : ''}`}>
        <div className={styles.message}>
          Are you sure you want to delete the call {" "}
          <span className={styles.name}>"{name}"?</span>
        </div>

        <div className={styles.actions}>
          <Button
            variant="primary"
            icon="paperflite-delete"
            text={deleting ? "Deleting..." : "Delete"}
            onClick={onConfirm}
          ></Button>
          <Button variant="secondary" text="Cancel" onClick={onCancel}></Button>
        </div>
      </div>
    </div>
  );
}; 

interface InProgressCallRecordingItemProps {
  uploadedFile: any;
  uploading: boolean;
  progress: number;
}

const InProgressCallRecordingItem: React.FC<InProgressCallRecordingItemProps> = ({ uploadedFile, uploading, progress }) => {
  return (
    <div className={styles.asset}>
      <Thumbnail
        media={null}
        defaultImage="/images/assets/default_asset.jpg"
        className={styles.icon}
      />
      <div className={styles.fileInfo}>
        <div className={styles.fileDetails}>
          <span className={styles.fileName}>{uploadedFile.name}</span>
          {uploading && (
            <p className={styles.description}>
              call upload in progress...
            </p>
          )}
        </div>
        {uploading && (
          <div className={styles.progressWrapper}>
            <ProgressBar now={progress} />
          </div>
        )}
      </div>
    </div>
  );
}

interface ProfileGenerationFailedProps {
  uploadedFile: any;
  onDelete: () => void; 
}

const ProfileGenerationFailed: React.FC<ProfileGenerationFailedProps> = ({ uploadedFile, onDelete }) => {
  return (
    <div className={styles.profileGenerationFailed}>
      {uploadedFile && (
        <div className={styles.icon}>
          <Thumbnail
            className={styles.icon}
            media={uploadedFile.icon}
            defaultImage="/images/assets/default_asset.jpg"
          />
          <i className="paperflite-danger" />
        </div>
      )}
      <div className={styles.details}>
      <div className={styles.info}>
        <div className={styles.name}>{uploadedFile.name}</div>
        <span className={styles.description}>
          upload failed — please try reuploading.
        </span>
      </div>

      <div className={styles.actionsContainer}>
        <button className={styles.deleteButton} onClick={onDelete}>
          <i className="paperflite-delete"></i>
        </button>
      </div>
      </div>

    </div>
  );
}

export const ProfileGenerationSteps = ({ simulation, onComplete }) => {
  const dispatch = useDispatch();

  const [currentStep, setCurrentStep] = useState(0);

  const { fetching, fetched, call } = useSelector((state: any) => {
    const callProfileState = _.get(state.coaching, "callRecordings.call") || {};

    const fetching = _.get(callProfileState, "loading", false);
    const fetched = _.get(callProfileState, "loaded", false);
    const call = _.get(callProfileState, "item", null);
    return {
      fetching: fetching,
      fetched: fetched,
      call: call,
    };
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentStep((prev) => prev + 1);
    }, 2000);

    return () => clearTimeout(timer);
  }, [currentStep]);

  useEffect(() => {
    if (fetched) {
      onComplete();

      dispatch(
        updateSimulationProperty({
          simulation: simulation.id!,
          property: "persona",
          value: call.persona,
        }),
      );
      dispatch(
        updateSimulationProperty({
          simulation: simulation.id!,
          property: "organization",
          value: call.organization,
        }),
      );
      dispatch(
        updateSimulationProperty({
          simulation: simulation.id!,
          property: "objectives",
          value: call.objective,
        }),
      );
    }
  }, [currentStep]);

  return (
    <div className={styles.loadingContainer}>
      <div className={styles.title}>
        generating <u>scenario details...</u>
      </div>
      <div className={styles.loadingWrapper}>
        <div className={styles.stepsWrapper}>
          <div className={styles.step}>
            <i
              className={cx("fa-solid fa-spinner fa-spin-pulse", styles.icon)}
            />
            <span className={cx(styles.text, { [styles.activeText]: true })}>
              extracting scenario details
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

interface CallRecordingItemProps {
  call: CallRecording;
  handleDelete: (call: CallRecording) => void;
  handleView: (call: CallRecording) => void;
}


const CallRecordingItem: React.FC<CallRecordingItemProps> = ({
  call,
  handleDelete,
  handleView,
}) => {
  const dispatch = useDispatch();

  const syncing = useSelector((state: any) => {
    return _.get(
      state.coaching,
      `callRecordings.call.syncing`,
      false,
    ) as boolean;
  });

  const createdBy = useMemo(() => {
    return _.get(call, "createdBy", null);
  }, [call]);

  const icon = useMemo(() => {
    return _.get(createdBy, "icon", null);
  }, [createdBy]);

  const name = useMemo(() => {
    return createdBy
      ? createdBy.firstName + " " + createdBy.lastName
      : "Unknown";
  }, [createdBy]);

  const transcriptProcessingStatus = useMemo(() => {
    if (!call.transcriptProcessing) {
      return "pending";
    }
    return call.transcriptProcessing.status;
  }, [call]);

  const profileProcessingStatus = useMemo(() => {
    if (!call.profileExtractionProcessing) {
      return "pending";
    }
    return call.profileExtractionProcessing.status;
  }, [call]);

  const callRecordingName = getCallRecordingName(call);

  const inProgress = !call.profileExtractionProcessing || call.profileExtractionProcessing?.status === "in_progress";

  const companyName = useMemo(() => {
    return call.companyName || call?.organization?.name || "no name";
  }, [call]);

  const buyerName = useMemo(() => {
    return call.buyerName || call?.persona?.name || "no name";
  }, [call]);

  const hasInsufficientData = useMemo(() => {
    const transcriptionFailed = transcriptProcessingStatus === "failed";
    const profileExtractionFailed = profileProcessingStatus === "failed";

    const organization = call?.organization;
    const persona = call?.persona;
    const objectives = call?.objective;

    return (
      transcriptionFailed ||
      profileExtractionFailed ||
      !organization ||
      !persona || 
      !objectives
    );
  }, [call]);

  useEffect(() => {
    if(hasInsufficientData && profileProcessingStatus === "completed"){
      if(call.id) {
        dispatch(syncCallRecordingProcessingState(call.id));
      }
    }
  }, [call.profileExtractionProcessing])

  return (
    <div className={styles.listContainer}>
      <div className={styles.metadatas}>
        <div className={styles.metaInfo}>
          <span className={styles.metadata}>
            {dateformat(call.createdDate, "dd mmm  hh:MM TT")}
          </span>
        </div>

        <div className={cx(styles.metaActions,{
            [styles.showOnHover]: !inProgress,
            [styles.show]: inProgress
          })}>
          {inProgress && (
            <i className={cx("fa-solid fa-spinner fa-spin-pulse", styles.spinner)}/>
          )}

          {!inProgress && (
            <>
              <IconButton
                className={styles.deleteIcon}
                icon="paperflite-delete"
                onClick={() => handleDelete(call)}
              />
              <div className={styles.divider} />
              <IconButton
                className={styles.viewIcon}
                icon="paperflite-right-arrow"
                onClick={() => handleView(call)}
              />
            </>
          )}
        </div>
      </div>

      <div className={styles.heading}>{callRecordingName}</div>
      {inProgress && (
        <p className={styles.description}>call processing in progress...</p>
      )}

      {!inProgress && !hasInsufficientData && (
        <div className={styles.details}>
          <div className={styles.detailRow}>
            <span className={styles.label}>Company name :</span>
            <span className={styles.label}>{companyName}</span>
          </div>

          <div className={styles.detailRow}>
            <span className={styles.label}>Buyer name :</span>
            <span className={styles.label}>{buyerName}</span>
          </div>
        </div>
      )}

      {/* Warning for insufficient data */}
      {/* Check for syncing */}
      {!inProgress && !syncing && hasInsufficientData && (
        <div className={styles.warning}>
          <i className="paperflite-danger" />
          <span className={styles.warningMessage}>insufficient data</span>
        </div>
      )}

      <div className={styles.footer}>
        <Thumbnail
          className={styles.avatar}
          media={icon}
          defaultImage="/images/assets/default_asset.jpg"
          width={32}
        />
        <span className={styles.name}>by {name}</span>
      </div>
    </div>
  );
};

export const CallRecordingPlaceholder = () => {
  return (
    <div className={cx(styles.listContainer, styles.callListplaceholder)}>
      <div className={styles.metadatas}>
        <div className={styles.meta} />
      </div>

      <div className={styles.headerContainer} />

      <div className={styles.details}>
        <div className={styles.detailRow}>
          <div className={styles.block} />
        </div>
        <div className={styles.detailRow}>
          <div className={styles.block} />
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.avatar} />
        <div className={styles.block} />
      </div>
    </div>
  );
};

const IconButton: React.FC<any> = ({ icon, title, onClick, disabled, className }) => {
  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  }
  return (
    <button disabled={disabled} onClick={handleClick} className={cx(styles.iconButton, className, {
      [styles.disabled]: disabled
    })}>
      <i className={icon}></i>
      <span className={styles.title}>{title}</span>
    </button>
  )
}

export const EraseTransition = (visible: boolean) => {
  return useTransition(visible, {
    from: {
      opacity: 0,
      width: 0,
      marginLeft: -10,
    },
    enter: {
      opacity: 1,
      width: 40,
      marginLeft: 0,
    },
    leave: {
      opacity: 0,
      width: 0,
      marginLeft: -10,
    },
    config: { tension: 300, friction: 30 },
  });
};

export const hasConfigurationData = (obj?: Record<string, any>): boolean => {
  if (!obj) return false;

  return Object.values(obj).some(value => {
    if (value === null || value === "" || _.size(value) === 0) return false;
    return true;
  });
};

